import React from 'react';
import { DEMO_IP_LOG } from '../api/demoData';
import Glossary, { GlossaryEntry } from '../components/Glossary';
import ComplianceBadges from '../components/ComplianceBadges';

const IP_GLOSSARY: GlossaryEntry[] = [
  { term: 'IP', definition: 'Investigational Product \u2014 the drug being tested in the clinical trial' },
  { term: 'Accountability', definition: 'Tracking every unit of drug from receipt to dispensing to return or destruction' },
  { term: 'Batch Number', definition: 'Manufacturing lot identifier \u2014 links dispensed drug back to production records' },
  { term: 'Storage Temp', definition: 'Temperature at time of dispensing \u2014 must remain within protocol-specified range (2\u20138\u00B0C)' },
  { term: 'Returned Units', definition: 'Unused tablets/capsules returned by patient \u2014 used to calculate compliance' },
  { term: 'Discrepancy', definition: 'Mismatch between expected and actual dispensing \u2014 requires investigation' },
];

const totalDispenses = DEMO_IP_LOG.length;
const correctCount = DEMO_IP_LOG.filter((d) => d.status === 'Correct').length;
const discrepancyCount = DEMO_IP_LOG.filter((d) => d.status === 'Discrepancy').length;

const SITE_COLORS: Record<string, string> = {
  'UCLA': '#38b2ac',
  'UCSF': '#4299e1',
  'Johns Hopkins': '#ed8936',
};

export default function IPAccountability() {
  return (
    <div style={styles.container}>
      <Glossary entries={IP_GLOSSARY} />
      <h2 style={styles.title}>IP Accountability</h2>
      <p style={styles.desc}>
        Investigational product dispensing log per 21 CFR 312.62. Tracks every unit of drug
        from pharmacy to patient with temperature monitoring and return reconciliation.
      </p>
      <ComplianceBadges badges={["ICH E6(R3) Annex 1", "21 CFR 312", "ALCOA"]} />

      {/* Summary cards */}
      <div style={styles.summaryRow}>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Total Dispenses</div>
          <div style={styles.summaryValue}>{totalDispenses}</div>
        </div>
        <div style={styles.summaryCard}>
          <div style={styles.summaryLabel}>Correct</div>
          <div style={{ ...styles.summaryValue, color: '#48bb78' }}>{correctCount}</div>
        </div>
        <div style={{ ...styles.summaryCard, border: discrepancyCount > 0 ? '1px solid #f56565' : '1px solid #2d3748' }}>
          <div style={styles.summaryLabel}>Discrepancies</div>
          <div style={{ ...styles.summaryValue, color: discrepancyCount > 0 ? '#f56565' : '#48bb78' }}>{discrepancyCount}</div>
        </div>
      </div>

      {/* IP Log Table */}
      <div style={{ overflowX: 'auto' }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>ID</th>
              <th style={styles.th}>Site</th>
              <th style={styles.th}>Patient</th>
              <th style={styles.th}>Visit</th>
              <th style={styles.th}>Dose</th>
              <th style={styles.th}>Batch</th>
              <th style={styles.th}>Temp (&deg;C)</th>
              <th style={styles.th}>Dispensed By</th>
              <th style={styles.th}>Timestamp</th>
              <th style={styles.th}>Returned</th>
              <th style={styles.th}>Status</th>
            </tr>
          </thead>
          <tbody>
            {DEMO_IP_LOG.map((d) => {
              const isDiscrepancy = d.status === 'Discrepancy';
              const siteColor = SITE_COLORS[d.site] || '#a0aec0';
              return (
                <tr key={d.id} style={isDiscrepancy ? { backgroundColor: 'rgba(245, 101, 101, 0.08)' } : {}}>
                  <td style={styles.td}>
                    <span style={{ color: '#63b3ed', fontWeight: 600 }}>{d.id}</span>
                  </td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '2px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                      backgroundColor: siteColor + '22', color: siteColor, border: `1px solid ${siteColor}`,
                    }}>{d.site}</span>
                  </td>
                  <td style={styles.td}>{d.patientId}</td>
                  <td style={styles.td}>Visit {d.visit}</td>
                  <td style={styles.td}><span style={{ fontWeight: 600 }}>{d.dose}</span></td>
                  <td style={styles.td}><code style={styles.code}>{d.batchNumber}</code></td>
                  <td style={styles.td}>
                    <span style={{ color: d.storageTempC >= 2 && d.storageTempC <= 8 ? '#48bb78' : '#f56565' }}>
                      {d.storageTempC.toFixed(1)}
                    </span>
                  </td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{d.dispensedBy}</td>
                  <td style={{ ...styles.td, fontSize: 12 }}>{new Date(d.timestamp).toLocaleString()}</td>
                  <td style={styles.td}>{d.returnedUnits > 0 ? <span style={{ color: '#ecc94b', fontWeight: 600 }}>{d.returnedUnits}</span> : '0'}</td>
                  <td style={styles.td}>
                    <span style={{
                      padding: '2px 10px', borderRadius: 12, fontSize: 11, fontWeight: 700,
                      backgroundColor: isDiscrepancy ? '#742a2a' : '#22543d',
                      color: isDiscrepancy ? '#fc8181' : '#48bb78',
                    }}>{d.status}</span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: { padding: 32 },
  title: { fontSize: 24, fontWeight: 700, color: '#e2e8f0', marginBottom: 8 },
  desc: { color: '#a0aec0', fontSize: 14, marginBottom: 24, maxWidth: 700 },
  summaryRow: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  summaryCard: { backgroundColor: '#1a202c', borderRadius: 8, padding: '12px 20px', minWidth: 140, flex: 1, border: '1px solid #2d3748' },
  summaryLabel: { color: '#718096', fontSize: 10, textTransform: 'uppercase', letterSpacing: 1 },
  summaryValue: { color: '#e2e8f0', fontSize: 28, fontWeight: 700, marginTop: 4 },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #2d3748', color: '#a0aec0', fontSize: 12, textTransform: 'uppercase', letterSpacing: 1, whiteSpace: 'nowrap' },
  td: { padding: '10px 12px', borderBottom: '1px solid #1a365d', color: '#e2e8f0', fontSize: 13 },
  code: { color: '#63b3ed', fontSize: 11, fontFamily: 'monospace', backgroundColor: '#171923', padding: '2px 6px', borderRadius: 4 },
};
